import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import './debug-api'; // Load debug functions

// Components
import Layout from './components/Layout/Layout';
import LoadingSpinner from './components/UI/LoadingSpinner';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import FiresPage from './pages/FiresPage';
import FireDetailPage from './pages/FireDetailPage';
import MapPage from './pages/MapPage';
import PhotosPage from './pages/PhotosPage';
import UploadPage from './pages/UploadPage';
import ProfilePage from './pages/ProfilePage';
import AdminPage from './pages/AdminPage';
import NotFoundPage from './pages/NotFoundPage';
import ApiDocs from './pages/ApiDocs';
import ModerationQueuePage from './pages/moderation/QueuePage';
import ModerationPhotoDetailPage from './pages/moderation/PhotoDetailPage';
import ModerationAuditPage from './pages/moderation/AuditPage';
import ModerationSettingsPage from './pages/moderation/SettingsPage';

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  const { isDark } = useTheme();
  const { isLoading } = useAuth();

  // Apply dark mode class to html element
  React.useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDark]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<LoginPage />} />

        {/* Protected Routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="fires" element={<FiresPage />} />
          <Route path="fires/:fireId" element={<FireDetailPage />} />
          <Route path="map" element={<MapPage />} />
          <Route path="photos" element={<PhotosPage />} />
          <Route path="upload" element={<UploadPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="/api-docs" element={<ApiDocs />} />

          {/* Moderação fogosapi */}
          <Route path="moderation" element={<Navigate to="/moderation/queue" replace />} />
          <Route path="moderation/queue" element={<ModerationQueuePage />} />
          <Route path="moderation/photos/:photoId" element={<ModerationPhotoDetailPage />} />
          <Route path="moderation/audit" element={<ModerationAuditPage />} />
          <Route
            path="moderation/settings"
            element={
              <ProtectedRoute adminOnly>
                <ModerationSettingsPage />
              </ProtectedRoute>
            }
          />

          {/* Admin Only Routes */}
          <Route
            path="admin"
            element={
              <ProtectedRoute adminOnly>
                <AdminPage />
              </ProtectedRoute>
            }
          />
        </Route>

        {/* 404 Page */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </div>
  );
}

export default App;
